// Initialize Sentry for error tracking (alongside PostHog for analytics)
import { initializeSentry } from './utils/sentry';
initializeSentry();

// Record first-touch attribution (UTMs/referrer/landing path) before the
// router can navigate away from the landing URL. Consent-independent: this is
// first-party form-attribution data (allowlisted, no ad-click IDs — see
// utils/firstTouch), only ever sent with a deliberate lead-form submission.
import { recordFirstTouch } from './utils/firstTouch';
recordFirstTouch();

import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { ThemeProvider, CssBaseline, CircularProgress, Box } from '@mui/material';
import '@fontsource/assistant/400.css';
import '@fontsource/assistant/500.css';
import '@fontsource/assistant/600.css';
import '@fontsource/assistant/700.css';
import { theme } from './styles/theme';
import { routeTree } from './routeTree.gen';
import './styles/index.css';

// Polyfills for older browsers
import 'resize-observer-polyfill';

// Web Vitals monitoring
import { reportWebVitals } from './lib/webVitals';
reportWebVitals({ debug: import.meta.env.DEV });

// Service Worker registration with proper error handling
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .catch((error) => {
        // Silently ignore ServiceWorker registration failures
        // These can be caused by:
        // - Browser extensions interfering with SW registration
        // - Privacy-focused browsers blocking SWs
        // - Corporate proxies/firewalls
        // - Bots/crawlers that don't support SWs
        // The app works fine without SW (just no offline support)
        console.debug('ServiceWorker registration failed (non-critical):', error);
      });
  });
}

// Loading fallback for lazy routes
function RouteLoadingFallback() {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '50vh',
      }}
    >
      <CircularProgress />
    </Box>
  );
}

const router = createRouter({
  routeTree,
  scrollRestoration: true,
  defaultPreloadStaleTime: 0,
  trailingSlash: 'preserve',
  defaultPendingComponent: RouteLoadingFallback,
  // Preload routes on hover for faster navigation
  defaultPreload: 'intent',
  defaultPreloadDelay: 100,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Suspense fallback={<RouteLoadingFallback />}>
        <RouterProvider router={router} />
      </Suspense>
    </ThemeProvider>
  </React.StrictMode>
);
